import * as React from "react";
import type { RecipeApiItem, RecipeStep } from "@recipevault/shared";
import { Button } from "../../ui/button.js";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface IngredientsPaneProps {
  recipe: RecipeApiItem;
  /** The currently active step, or null if between steps / at start. */
  step: RecipeStep | null;
  /** IDs of ingredients the user has manually checked off. */
  checkedIngredients: string[];
  onToggleChecked: (ingredientId: string) => void;
}

// ---------------------------------------------------------------------------
// Heuristic: name-overlap ingredient detection
//
// The step model (Plan 06a) has NO per-step ingredient references — there is no
// `ingredientIds` field on RecipeStep. We therefore approximate "used in this
// step" by case-insensitive substring match of the ingredient's `name` against
// the step's `text`. This is a heuristic: it can produce false positives (e.g.
// "oil" matching "boil") and false negatives (aliases, pronouns). A future plan
// could add explicit ingredient refs to RecipeStep to replace this logic.
// ---------------------------------------------------------------------------

function ingredientUsedInStep(
  ingredientName: string,
  stepText: string,
): boolean {
  const lower = stepText.toLowerCase();
  const name = ingredientName.toLowerCase();
  // Word-boundary-ish: check if the name appears as a word or phrase in the text.
  // We use a simple word-wrapped regex: (?<![a-z])name(?![a-z])
  const re = new RegExp(`(?<![a-z])${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?![a-z])`);
  return re.test(lower);
}

/** Format ingredient for display: prefer `raw` if present; fall back to qty + unit + name. */
function formatIngredient(ing: RecipeApiItem["ingredients"][number]): string {
  if (ing.raw) return ing.raw;
  const parts: string[] = [];
  if (ing.qty != null) parts.push(String(ing.qty));
  if (ing.unit) parts.push(ing.unit);
  parts.push(ing.name);
  return parts.join(" ");
}

// ---------------------------------------------------------------------------
// IngredientsPane
// ---------------------------------------------------------------------------

/**
 * Bottom pane of cooking mode (spec §6.4).
 *
 * Default view: subset of ingredients whose name appears in the current step's
 * text (name-overlap heuristic — see comment above). If the subset is empty or
 * step is null, falls back to the full list.
 *
 * "Show all" toggle expands to the full ingredient checklist (and back).
 *
 * Ingredients used in the current step are visually highlighted in both views.
 * Checking is fully manual; checked state is managed by the parent.
 */
export function IngredientsPane({
  recipe,
  step,
  checkedIngredients,
  onToggleChecked,
}: IngredientsPaneProps) {
  const [expanded, setExpanded] = React.useState(false);

  const { ingredients } = recipe;

  // Compute which ingredient ids are relevant to the current step (heuristic)
  const stepRelevantIds = React.useMemo<Set<string>>(() => {
    if (!step) return new Set();
    const ids = new Set<string>();
    for (const ing of ingredients) {
      if (ingredientUsedInStep(ing.name, step.text)) {
        ids.add(ing.id);
      }
    }
    return ids;
  }, [step, ingredients]);

  // Subset = ingredients relevant to the current step; fall back to all
  const subset = React.useMemo(
    () =>
      stepRelevantIds.size > 0
        ? ingredients.filter((ing) => stepRelevantIds.has(ing.id))
        : ingredients,
    [ingredients, stepRelevantIds],
  );

  // Shown list depends on expanded state
  const displayed = expanded ? ingredients : subset;

  // Whether the toggle button makes sense (only show if subset < full list)
  const canToggle = stepRelevantIds.size > 0 && subset.length < ingredients.length;

  return (
    <div role="region" aria-label="Ingredients" className="bg-zinc-800 px-4 py-3">
      {/* Header row */}
      <div className="mb-2 flex items-center justify-between">
        <p className="text-sm font-semibold text-zinc-100">
          Ingredients
          {!expanded && stepRelevantIds.size > 0 && (
            <span className="ml-1 text-xs font-normal text-zinc-400">
              (this step)
            </span>
          )}
        </p>
        {canToggle && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-amber-400 hover:bg-zinc-700 hover:text-amber-300"
          >
            {expanded ? "Show current step" : "Show all"}
          </Button>
        )}
      </div>

      {/* Ingredient list */}
      <ul className="flex flex-col gap-1">
        {displayed.map((ing) => {
          const isChecked = checkedIngredients.includes(ing.id);
          const isHighlighted = stepRelevantIds.has(ing.id);

          return (
            <li key={ing.id}>
              <Button
                variant="ghost"
                aria-label={formatIngredient(ing)}
                aria-pressed={isChecked}
                className={`min-h-[48px] w-full justify-start gap-3 px-2 text-left text-base hover:bg-zinc-700 ${
                  isHighlighted ? "text-amber-200" : "text-zinc-100"
                }`}
                onClick={() => onToggleChecked(ing.id)}
              >
                {/* Checkbox indicator */}
                <span
                  aria-hidden
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border-2 transition-colors ${
                    isChecked
                      ? "border-emerald-500 bg-emerald-500 text-white"
                      : isHighlighted
                        ? "border-amber-400 bg-transparent"
                        : "border-zinc-500 bg-transparent"
                  }`}
                >
                  {isChecked && (
                    <svg
                      viewBox="0 0 12 12"
                      fill="none"
                      className="h-3 w-3"
                      aria-hidden
                    >
                      <path
                        d="M2 6l3 3 5-5"
                        stroke="currentColor"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                {/* Plain text — never innerHTML */}
                <span
                  className={
                    isChecked
                      ? "text-zinc-400 line-through"
                      : isHighlighted
                        ? "font-medium text-amber-200"
                        : "text-zinc-100"
                  }
                >
                  {formatIngredient(ing)}
                </span>
              </Button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
